import axios, { AxiosResponse } from 'axios';

interface RefreshResponse {
  data?: {
    expiresAt?: string;
  };
}

// API 기본 설정 - 동적으로 환경에 따라 결정
const getApiBaseUrl = () => {
  // 환경변수가 설정되어 있으면 우선 사용
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // 현재 호스트가 IP 주소인 경우 (Flutter 앱에서 접근)
  const currentHost = window.location.hostname;
  if (currentHost !== 'localhost' && currentHost !== '127.0.0.1') {
    return `http://${currentHost}/api`;
  }

  // 기본값 (웹 브라우저에서 localhost 접근)
  return 'http://localhost:3002/api';
};

export const API_BASE_URL = getApiBaseUrl();

// API 인스턴스 생성
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
  withCredentials: true, // 쿠키 전송을 위해 필요
});

// 토큰 만료 시간 관리
let tokenExpiry: number | null = null;
let refreshTimer: NodeJS.Timeout | null = null;

// 토큰 갱신 상태 관리 (무한 루프 방지)
let isRefreshing = false;
let refreshPromise: Promise<AxiosResponse<RefreshResponse>> | null = null;

// 토큰 만료 시간 설정 및 자동 리프레시 스케줄링
export const setTokenExpiry = (expiryTime: number) => {
  tokenExpiry = expiryTime;
  scheduleTokenRefresh();
};

// 30분(1800초) 전에 토큰 리프레시 스케줄링
const scheduleTokenRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }

  if (tokenExpiry) {
    const now = Date.now();
    const timeUntilRefresh = tokenExpiry - now - (30 * 60 * 1000); // 30분 전

    if (timeUntilRefresh > 0) {
      refreshTimer = setTimeout(async () => {
        try {
          const response = await api.post('/auth/refresh');

          // 새로운 토큰 만료 시간이 응답에 포함되어 있다면 업데이트
          if (response.data?.data?.expiresAt) {
            setTokenExpiry(new Date(response.data.data.expiresAt).getTime());
          }
        } catch {
          // 자동 리프레시 실패 시 로그아웃 처리하지 않고 다음 요청에서 처리하도록 함
        }
      }, timeUntilRefresh);
    }
  }
};

// 토큰 정리 (로그아웃 시 호출)
export const clearTokenTimer = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  tokenExpiry = null;
  isRefreshing = false;
  refreshPromise = null;
};

// 요청 인터셉터 - 토큰 만료 시간 확인
api.interceptors.request.use(
  (config) => {
    // 토큰 검증 요청은 중복 방지 로직에서 제외
    if (config.url?.includes('/auth/verify')) {
      return config;
    }

    // 토큰이 곧 만료될 예정이면 먼저 리프레시
    if (tokenExpiry && !config.url?.includes('/auth/refresh')) {
      const now = Date.now();
      const timeUntilExpiry = tokenExpiry - now;

      // 2분 이내에 만료될 예정이면 즉시 리프레시 (기존 5분에서 2분으로 조정)
      if (timeUntilExpiry < 2 * 60 * 1000 && timeUntilExpiry > 0) {
        // 비동기 리프레시를 시도하되, 실패해도 원래 요청은 진행
        api.post('/auth/refresh').catch(() => { });
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// 응답 인터셉터 - 에러 처리 및 토큰 갱신
api.interceptors.response.use(
  (response) => {
    // 302 Found 응답을 에러로 처리
    if (response.status === 302) {
      const error = new Error('Redirect response') as Error & { response: { status: number; data: { message: string } } };
      error.response = { status: 302, data: { message: '존재하지 않은 계정이거나 등록되지 않은 이메일입니다.' } };
      return Promise.reject(error);
    }

    // 로그인 응답에서 토큰 만료 시간 추출
    if (response.config.url?.includes('/auth/login') && response.data?.data?.expiresAt) {
      setTokenExpiry(new Date(response.data.data.expiresAt).getTime());
    }

    // 리프레시 응답에서 새 토큰 만료 시간 추출
    if (response.config.url?.includes('/auth/refresh') && response.data?.data?.expiresAt) {
      setTokenExpiry(new Date(response.data.data.expiresAt).getTime());
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 로그아웃 요청이나 로그인 요청은 인터셉터에서 제외
    if (originalRequest.url?.includes('/auth/logout') ||
      originalRequest.url?.includes('/login') ||
      originalRequest.url?.includes('/auth/refresh')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // 이미 토큰 갱신 중인 경우 기존 Promise 재사용
      if (isRefreshing && refreshPromise) {
        try {
          await refreshPromise;
          return api(originalRequest);
        } catch (refreshError) {
          clearTokenTimer();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      // 토큰 갱신 시작
      isRefreshing = true;
      refreshPromise = api.post('/auth/refresh');

      try {
        const refreshResponse = await refreshPromise;

        // 새로운 토큰 만료 시간 설정
        if (refreshResponse.data?.data?.expiresAt) {
          setTokenExpiry(new Date(refreshResponse.data.data.expiresAt).getTime());
        }

        isRefreshing = false;
        refreshPromise = null;
        
        return api(originalRequest);
      } catch (refreshError) {
        // 리프레시 토큰도 만료된 경우
        isRefreshing = false;
        refreshPromise = null;
        clearTokenTimer();
        
        // 즉시 로그아웃 처리하여 무한 루프 방지
        document.cookie = 'accessToken=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        document.cookie = 'refreshToken=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        
        // 현재 경로가 로그인 페이지가 아닌 경우에만 리다이렉트
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;